import { SociosSocketEvent, type SocketsListener, useSocketEventListener, withRefreshStrategy } from '@capsicum/common';
import { useMainCurrenciesQuery, useMe } from '@capsicum/socios-core';
import { useMemo } from 'react';

import { useBidListQueries } from '../data-access';
import { UseAuctionDetailsQueryKey } from '../data-access/queries';

export const useBidHistory = ({ auctionId }: { auctionId: string }) => {
	const { me } = useMe();
	const { data: currencies } = useMainCurrenciesQuery();

	const {
		data: bidList,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isLoading,
		isRefetching,
		refetch: refetchBidList
	} = useBidListQueries({
		altCurrencyId: currencies?.altCurrency?.id || '',
		auctionId: auctionId || '',
		limit: 12,
		mainCurrencyId: currencies?.defaultCurrency?.id || ''
	});

	const bidListStructureData = bidList
		?.map(bid => {
			return {
				amount: bid.altCurrency?.bidAmount ? +bid.altCurrency.bidAmount : 0,
				amountChz: bid.mainCurrency?.bidAmount ? +bid.mainCurrency.bidAmount : 0,
				date: bid.bidReceivedAt,
				iso2Country: bid.countryCode,
				isOwnBid: bid.participant?.chilizUserId === me.externalUserId,
				mwsUserId: bid.obfuscatedUserId,
				status: bid.status
			};
		})
		.filter(bid => bid.status === 'accepted' || bid.status === 'outbidded' || bid.status === 'validated');
	const chzShortCode = currencies?.defaultCurrency?.shortCode || '';
	const fiatShortCode = currencies?.altCurrency?.shortCode || '';

	const listeners = useMemo(
		() =>
			new Map<SociosSocketEvent, SocketsListener>([
				[
					SociosSocketEvent.COLLECTIBLE_BID_STATUS_UPDATED,
					withRefreshStrategy({
						cacheKeys: [
							...UseAuctionDetailsQueryKey('auctionDetails'),
							...UseAuctionDetailsQueryKey('bidList')
						],
						listener: refetchBidList
					})
				]
			]),
		[refetchBidList]
	);

	useSocketEventListener(listeners);

	return {
		bidListStructureData,
		chzShortCode,
		fetchNextPage,
		fiatShortCode,
		hasNextPage,
		isFetching,
		isLoading,
		isRefetching,
		refetchBidList
	};
};
